// The thinnest line worth painting, in world units: below about this, a stroke lands under a texel of the
// picture the track is drawn into and flickers while driving (see drawFencePlinth).
const MIN_BAND = 3;

/** Paint a vertical, transparent fence tile along a polyline, preserving its aspect ratio and
 * repeat phase through bends. Shared by the live editor and the game's baked track painter. */
export function drawFence(ctx, points, thickness, image, plinth = null) {
  const width = Number(thickness);
  if (!(width > 0) || !Number.isFinite(width)) return;
  const run = [];
  for (const point of points ?? []) {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) continue;
    const last = run.at(-1);
    if (!last || Math.hypot(point.x - last.x, point.y - last.y) > 1e-6) run.push(point);
  }
  if (run.length < 2) return;
  if (plinth) drawFencePlinth(ctx, run, width, plinth);
  const directions = run.slice(1).map((p, i) => {
    const a = run[i], length = Math.hypot(p.x - a.x, p.y - a.y);
    return { x: (p.x - a.x) / length, y: (p.y - a.y) / length, length };
  });
  const closed = run.length > 2 && Math.hypot(run[0].x - run.at(-1).x, run[0].y - run.at(-1).y) < 1e-6;
  const joins = run.map((p, i) => {
    const before = directions[i - 1] ?? (closed ? directions.at(-1) : directions[0]);
    const after = directions[i] ?? (closed ? directions[0] : directions.at(-1));
    const nx = -before.y - after.y, ny = before.x + after.x;
    const dot = nx * -after.y + ny * after.x;
    // Bound sharp turns so a folded fence never grows a long spike.
    const factor = dot > 1e-6 ? Math.min(width / 2 / dot, width * 2 / Math.hypot(nx, ny)) : 0;
    return { x: nx * factor, y: ny * factor };
  });
  const ready = image?.width > 0 && image?.height > 0;
  const scale = ready ? width / image.width : 1;
  const period = ready ? image.height * scale : width * 2.7;
  let distance = 0;
  ctx.save();
  for (let i = 0; i < directions.length; i++) {
    const a = run[i], b = run[i + 1], d = directions[i];
    const ja = joins[i], jb = joins[i + 1];
    const corners = [
      { x: a.x + ja.x, y: a.y + ja.y }, { x: b.x + jb.x, y: b.y + jb.y },
      { x: b.x - jb.x, y: b.y - jb.y }, { x: a.x - ja.x, y: a.y - ja.y },
    ];
    const along = corners.map(p => (p.x - a.x) * d.x + (p.y - a.y) * d.y);
    const start = Math.min(...along), end = Math.max(...along);
    ctx.save();
    ctx.beginPath();
    corners.forEach((p, j) => j ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y));
    ctx.closePath();
    ctx.clip();
    ctx.translate(a.x, a.y);
    ctx.rotate(Math.atan2(d.y, d.x) - Math.PI / 2);
    if (ready) {
      // Tile origin follows distance along the WHOLE path, never restarting at sampled curve nodes.
      const first = Math.floor((distance + start) / period) * period - distance;
      for (let y = first; y < end; y += period) ctx.drawImage(image, -width / 2, y, width, period);
    } else {
      // A sparse metal fallback while loading; the space between rails remains transparent.
      ctx.strokeStyle = '#a2a7aa';
      ctx.lineWidth = Math.max(0.5, width * 0.07);
      ctx.beginPath();
      for (const x of [-width * 0.12, width * 0.12]) {
        ctx.moveTo(x, start); ctx.lineTo(x, end);
      }
      const first = Math.ceil((distance + start - period / 2) / period) * period + period / 2 - distance;
      for (let y = first; y < end; y += period) {
        ctx.moveTo(-width * 0.4, y); ctx.lineTo(width * 0.4, y);
      }
      ctx.stroke();
    }
    ctx.restore();
    distance += d.length;
  }
  ctx.restore();
}

/** A shallow concrete wall under the rail: square ends, continuous mitred bends, a narrow shadow
 * and a light top face. Paint the whole base first so no segment can cover the fence at a joint. */
function drawFencePlinth(ctx, run, width, look) {
  const wallWidth = width * look.width;
  ctx.save();
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';
  ctx.miterLimit = 4;
  const path = () => {
    ctx.beginPath();
    run.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y));
  };
  ctx.save();
  ctx.translate(width * 0.08, width * 0.12);
  path();
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.24)';
  ctx.lineWidth = wallWidth;
  ctx.stroke();
  ctx.restore();
  path();
  // The three bands are stroked widest-first, so each one leaves a ring of the colour before it: the
  // contour shows as (wallWidth - base) / 2, the base as (base - crown) / 2. Those rings are what has to
  // stay visible — a line under a texel of the picture (0.66 px per world unit on a big map, less again
  // when the camera pulls back) appears and disappears as the camera moves. So the two inner widths are
  // pulled IN from the outside until every ring has MIN_BAND to show.
  const base = Math.max(0, Math.min(wallWidth * 0.86, wallWidth - MIN_BAND * 2));
  const crown = Math.max(0, Math.min(wallWidth * 0.60, base - MIN_BAND * 2));
  for (const [colour, size] of [[look.contour, wallWidth], [look.base, base], [look.crown, crown]]) {
    ctx.strokeStyle = colour;
    ctx.lineWidth = size;
    ctx.stroke();
  }
  // Fine concrete aggregate, deterministic along the path and independent of screen resolution.
  let distance = 0;
  const step = Math.max(1, wallWidth * 0.24);
  for (let i = 1; i < run.length; i++) {
    const a = run[i - 1], b = run[i], length = Math.hypot(b.x - a.x, b.y - a.y);
    const dx = (b.x - a.x) / length, dy = (b.y - a.y) / length;
    for (let at = Math.ceil(distance / step) * step - distance; at < length; at += step) {
      const hash = Math.imul(Math.round((distance + at) / step) + 1, 2654435761) >>> 0;
      const across = ((hash % 101) / 100 - 0.5) * wallWidth * 0.6;
      ctx.fillStyle = hash & 1 ? 'rgba(64, 62, 53, 0.20)' : 'rgba(245, 241, 218, 0.20)';
      const size = Math.max(MIN_BAND * 0.7, wallWidth * (0.015 + (hash % 5) * 0.007));
      ctx.fillRect(a.x + dx * at - dy * across - size / 2,
        a.y + dy * at + dx * across - size / 2, size, size);
    }
    distance += length;
  }
  ctx.restore();
}
